import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  UserOrganization,
  AddUserOrganizationInput,
  UpdateUserOrganizationInput,
} from "../types";

const USER_ORGANIZATION_FIELDS = `
  id
  userId
  organizationId
  roles
  modules
  status
  employeeType
  joinedAt
  invitedBy
  createdAt
  updatedAt
`;

export const userOrganizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    organizationUsers: builder.query<UserOrganization[], string>({
      query: (organizationId) => ({
        body: {
          query: `
            query OrganizationUsers($organizationId: String!) {
              organizationUsers(organizationId: $organizationId) {
                ${USER_ORGANIZATION_FIELDS}
              }
            }
          `,
          variables: { organizationId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<UserOrganization[]>(res, "organizationUsers"),

      providesTags: (result, _err, organizationId) =>
        result
          ? [
              ...result.map((m) => ({
                type: "UserOrganization" as const,
                id: m.id,
              })),
              { type: "UserOrganization" as const, id: `ORG-${organizationId}` },
            ]
          : [{ type: "UserOrganization" as const, id: `ORG-${organizationId}` }],
    }),

    userOrganizations: builder.query<UserOrganization[], string>({
      query: (userId) => ({
        body: {
          query: `
            query UserOrganizations($userId: String!) {
              userOrganizations(userId: $userId) {
                ${USER_ORGANIZATION_FIELDS}
              }
            }
          `,
          variables: { userId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<UserOrganization[]>(res, "userOrganizations"),

      providesTags: (result, _err, userId) =>
        result
          ? [
              ...result.map((m) => ({
                type: "UserOrganization" as const,
                id: m.id,
              })),
              { type: "UserOrganization" as const, id: `USER-${userId}` },
            ]
          : [{ type: "UserOrganization" as const, id: `USER-${userId}` }],
    }),

    addUserToOrganization: builder.mutation<
      UserOrganization,
      AddUserOrganizationInput
    >({
      query: (input) => ({
        body: {
          query: `
            mutation AddUserToOrganization($input: AddUserOrganizationInput!) {
              addUserToOrganization(input: $input) {
                ${USER_ORGANIZATION_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<UserOrganization>(res, "addUserToOrganization"),

      invalidatesTags: (result, _err, input) => [
        { type: "UserOrganization", id: `ORG-${input.organizationId}` },
        { type: "UserOrganization", id: `USER-${input.userId}` },
        { type: "User", id: input.userId },
        ...(result ? [{ type: "UserOrganization" as const, id: result.id }] : []),
      ],
    }),

    updateUserOrganization: builder.mutation<
      UserOrganization,
      { id: string; input: UpdateUserOrganizationInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateUserOrganization(
              $id: String!
              $input: UpdateUserOrganizationInput!
            ) {
              updateUserOrganization(id: $id, input: $input) {
                ${USER_ORGANIZATION_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<UserOrganization>(res, "updateUserOrganization"),

      invalidatesTags: (result, _err, { id }) => [
        { type: "UserOrganization", id },
        ...(result
          ? [
              {
                type: "UserOrganization" as const,
                id: `ORG-${result.organizationId}`,
              },
              { type: "UserOrganization" as const, id: `USER-${result.userId}` },
            ]
          : []),
      ],
    }),

    removeUserFromOrganization: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation RemoveUserFromOrganization($id: String!) {
              removeUserFromOrganization(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<boolean>(res, "removeUserFromOrganization"),

      invalidatesTags: (_result, _err, id) => [
        { type: "UserOrganization", id },
      ],
    }),
  }),
});

export const {
  useOrganizationUsersQuery,
  useLazyOrganizationUsersQuery,
  useUserOrganizationsQuery,
  useLazyUserOrganizationsQuery,
  useAddUserToOrganizationMutation,
  useUpdateUserOrganizationMutation,
  useRemoveUserFromOrganizationMutation,
} = userOrganizationApi;
